import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, theme, Avatar, Dropdown, Space, Divider } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  FileTextOutlined,
  UserOutlined,
  SearchOutlined,
  BookOutlined,
  SettingOutlined,
  DashboardOutlined,
  BellOutlined,
  LogoutOutlined,
  HomeOutlined,
  FileSearchOutlined,
  EditOutlined,
  StarOutlined,
  DownOutlined,
  MailOutlined,
  GlobalOutlined,
  DatabaseOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import { ROLES, hasRole, hasAnyRole } from '../../constants/roles';
import {globalStyles} from "../../theme";
import { getProfilePictureUrl } from '../../utils/imageUtils';
import NotificationCenter from '../notification/NotificationCenter';

const { Header, Sider, Content } = Layout;

const MainLayout = ({ children }) => {
  // Set breakpoint for auto-collapsing sidebar
  const SIDEBAR_COLLAPSE_BREAKPOINT = 1080;

  // Set initial collapsed state based on window width
  const initialWindowWidth = window.innerWidth;

  const [collapsed, setCollapsed] = useState(initialWindowWidth <= SIDEBAR_COLLAPSE_BREAKPOINT);
  const [windowWidth, setWindowWidth] = useState(initialWindowWidth);
  const [isAutoCollapse, setIsAutoCollapse] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const {
    token: { colorBgContainer, borderRadiusLG, colorPrimary },
  } = theme.useToken();

  // Update window width when window is resized
  useEffect(() => {
    let resizeTimer;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);

      // Switch back to auto mode after a short delay
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setIsAutoCollapse(true);
      }, 500); // 500ms delay
    };

    window.addEventListener('resize', handleResize);

    // Clean up event listener and timer on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Auto-collapse sidebar based on window width
  useEffect(() => {
    if (isAutoCollapse) {
      if (windowWidth <= SIDEBAR_COLLAPSE_BREAKPOINT) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    }
  }, [windowWidth, isAutoCollapse]);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      console.log('Logout clicked');
      logout();
      navigate('/');
    } else {
      navigate(`/${key}`);
    }
  };

  const logoStyle = {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    padding: collapsed ? '0' : '0 24px',
    color: '#fff',
    fontSize: collapsed ? '14px' : '18px',
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  };

  const menuItems = [];
  const overviewItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
  ];

  const publishingItems = [
    {
      key: 'my-submissions',
      icon: <EditOutlined />,
      label: 'My Submissions',
    },
  ];

  if (hasRole(user, ROLES.CHIEF_EDITOR)) {
    publishingItems.push({
      key: 'journal-manuscripts',
      icon: <FileSearchOutlined />,
      label: 'Journal Manuscripts',
    });
  }

  if (hasAnyRole(user, [ROLES.CHIEF_EDITOR, ROLES.ADMIN])) {
    publishingItems.push({
      key: 'publications',
      icon: <StarOutlined />,
      label: 'Article Management',
    });
  }

  const journalItems = [
    {
      key: 'journals',
      icon: <FileSearchOutlined />,
      label: 'Browse Journals',
    },
  ];

  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.CHIEF_EDITOR])) {
    journalItems.push({
      key: 'journals/manage',
      icon: <SettingOutlined />,
      label: 'Manage Journals',
    });
  }

  if (hasAnyRole(user, [ROLES.EDITOR, ROLES.CHIEF_EDITOR])) {
    journalItems.push({
      key: 'journals/volumes',
      icon: <BookOutlined />,
      label: 'Volumes & Issues',
    });
  }

  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.CHIEF_EDITOR])) {
    journalItems.push({
      key: 'journals/approvals',
      icon: <StarOutlined />,
      label: 'Journal Approvals',
    });
  }

  if (hasAnyRole(user, [ROLES.MANAGING_CHIEF_EDITOR])) {
    journalItems.push({
      key: 'doi/management',
      icon: <DatabaseOutlined />,
      label: 'DOI Management',
    });
  }

  const reviewItems = [];
  if (hasRole(user, ROLES.REVIEWER)) {
    reviewItems.push({
      key: 'reviews',
      icon: <EditOutlined />,
      label: 'Reviews',
    });
  }

  const accountItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
  ];

  const adminItems = [];
  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR])) {
    adminItems.push({
      key: 'admin/configuration',
      icon: <SettingOutlined />,
      label: 'Configuration',
    });
    adminItems.push({
      key: 'admin/users',
      icon: <UserOutlined />,
      label: 'User Management',
    });
  }

  if (hasAnyRole(user, [ROLES.ADMIN])) {
    adminItems.push({
      key: 'admin/doi',
      icon: <DatabaseOutlined />,
      label: 'DOI Management',
    });
  }

  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR])) {
    adminItems.push({
      key: 'admin/committee',
      icon: <GlobalOutlined />,
      label: 'Committee',
    });
  }

  if (hasRole(user, ROLES.MANAGING_CHIEF_EDITOR)) {
    adminItems.push({
      key: 'admin/email-templates',
      icon: <MailOutlined />,
      label: 'Email Templates',
    });
  }

  if (hasAnyRole(user, [ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR])) {
    adminItems.push({
      key: 'admin/announcements',
      icon: <BellOutlined />,
      label: 'Announcements',
    });
  }

  if (overviewItems.length) {
    menuItems.push({
      key: 'overview-group',
      label: 'Overview',
      type: 'group',
      children: overviewItems,
    });
  }

  if (publishingItems.length) {
    menuItems.push({
      key: 'publishing-group',
      label: 'Publishing',
      type: 'group',
      children: publishingItems,
    });
  }

  if (journalItems.length) {
    menuItems.push({
      key: 'journals-group',
      label: 'Journals',
      type: 'group',
      children: journalItems,
    });
  }

  if (reviewItems.length) {
    menuItems.push({
      key: 'reviews-group',
      label: 'Reviews',
      type: 'group',
      children: reviewItems,
    });
  }

  const resourceItems = [
    {
      key: 'documentation',
      icon: <QuestionCircleOutlined />,
      label: 'Documentation',
    },
  ];

  if (accountItems.length) {
    menuItems.push({
      key: 'account-group',
      label: 'Account',
      type: 'group',
      children: accountItems,
    });
  }

  if (resourceItems.length) {
    menuItems.push({
      key: 'resources-group',
      label: 'Resources',
      type: 'group',
      children: resourceItems,
    });
  }

  if (adminItems.length) {
    menuItems.push({
      key: 'admin-group',
      label: 'Admin',
      type: 'group',
      children: adminItems,
    });
  }

  const handleMenuClick = ({ key }) => {
    // Don't navigate for group headers
    if (key.endsWith('-header')) {
      return;
    }
    navigate(`/${key}`);
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={260}
        collapsedWidth={80}
        style={{
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          borderRight: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 6px 24px rgba(15, 23, 42, 0.08)'
        }}
      >
        <Menu
          theme="light"
          mode="inline"
          defaultSelectedKeys={[location.pathname.split('/')[1] || 'browse']}
          items={menuItems}
          onClick={handleMenuClick}
          style={{
            border: 'none',
            padding: '4px 0 16px',
            overflow: 'hidden'
          }}
          className={`sidebar-menu ${collapsed ? 'collapsed-menu' : ''}`}
        />
        <style>
          {`
            .sidebar-menu.ant-menu-light {
              background: transparent;
              border: none;
            }
            .sidebar-menu.ant-menu-light .ant-menu-sub {
              background: transparent !important;
            }
            .sidebar-menu.ant-menu-light .ant-menu-item,
            .sidebar-menu.ant-menu-light .ant-menu-submenu-title {
              height: 44px;
              line-height: 44px;
              margin: 4px 12px;
              border-radius: 10px;
              padding: 0 16px !important;
              width: calc(100% - 24px);
              transition: all 0.2s ease;
              position: relative;
              overflow: hidden;
              color: #334155;
              font-weight: 500;
            }

            .sidebar-menu.ant-menu-light .ant-menu-item:hover,
            .sidebar-menu.ant-menu-light .ant-menu-submenu-title:hover {
              background: rgba(24, 144, 255, 0.08) !important;
              transform: translateX(2px);
              box-shadow: 0 6px 14px rgba(24, 144, 255, 0.12);
              color: #1d4ed8;
            }

            .sidebar-menu.ant-menu-light .ant-menu-item-selected {
              background: linear-gradient(135deg, rgba(24, 144, 255, 0.16) 0%, rgba(9, 109, 217, 0.08) 100%) !important;
              color: #0f172a !important;
              font-weight: 600;
              box-shadow: 0 8px 20px rgba(24, 144, 255, 0.16);
              transform: translateX(2px);
            }
            .sidebar-menu.ant-menu-light .ant-menu-item-selected::before {
              content: '';
              position: absolute;
              left: 0;
              top: 50%;
              transform: translateY(-50%);
              width: 4px;
              height: 18px;
              background: #1890ff;
              border-radius: 0 2px 2px 0;
            }

            .sidebar-menu.ant-menu-light .ant-menu-sub .ant-menu-item {
              height: 40px;
              line-height: 40px;
              margin: 4px 16px;
              padding: 0 14px !important;
              width: calc(100% - 32px);
              border-radius: 8px;
              background: rgba(15, 23, 42, 0.02);
            }
            .sidebar-menu.ant-menu-light .ant-menu-sub .ant-menu-item:hover {
              background: rgba(24, 144, 255, 0.1) !important;
              transform: translateX(2px);
            }
            .sidebar-menu.ant-menu-light .ant-menu-sub .ant-menu-item-selected {
              background: rgba(24, 144, 255, 0.18) !important;
              color: #1d4ed8;
              font-weight: 600;
            }

            .sidebar-menu.ant-menu-light .ant-menu-item .anticon,
            .sidebar-menu.ant-menu-light .ant-menu-submenu-title .anticon {
              font-size: 18px;
              vertical-align: middle;
              min-width: 20px;
              color: #64748b;
              transition: all 0.2s ease;
            }
            .sidebar-menu.ant-menu-light .ant-menu-item:hover .anticon,
            .sidebar-menu.ant-menu-light .ant-menu-submenu-title:hover .anticon {
              color: #1890ff;
              transform: scale(1.08);
            }
            .sidebar-menu.ant-menu-light .ant-menu-item-selected .anticon {
              color: #0f172a;
              transform: scale(1.08);
            }

            .sidebar-menu.ant-menu-light .ant-menu-item-icon + span,
            .sidebar-menu.ant-menu-light .ant-menu-submenu-title .ant-menu-title-content {
              margin-left: 12px;
            }

            /* Collapsed menu styles */
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-item {
              padding: 0 calc(50% - 20px) !important;
              text-align: center;
              margin: 6px 16px;
              width: calc(100% - 32px);
            }
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
              padding: 0 calc(50% - 20px) !important;
              text-align: center;
              margin: 6px 16px;
              width: calc(100% - 32px);
            }
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-item .anticon,
            .collapsed-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .anticon {
              margin: 0;
              font-size: 20px;
              line-height: 48px;
            }

            /* Submenu arrow styling */
            .sidebar-menu.ant-menu-light .ant-menu-submenu-arrow {
              color: rgba(71, 85, 105, 0.6);
              transition: all 0.3s ease;
            }
            .sidebar-menu.ant-menu-light .ant-menu-submenu:hover .ant-menu-submenu-arrow {
              color: #1890ff;
            }

            /* Menu group headers */
            .sidebar-menu.ant-menu-light .ant-menu-item-group-title {
              color: #64748b !important;
              font-size: 12px !important;
              font-weight: 700 !important;
              padding: 18px 20px 8px 20px !important;
              margin: 12px 12px 0 12px !important;
              border-bottom: 1px solid rgba(15, 23, 42, 0.08);
              text-transform: uppercase;
              letter-spacing: 0.8px;
            }

            .sidebar-menu.ant-menu-light .ant-menu-item-group-title:first-child {
              margin-top: 8px !important;
            }

            /* Indented menu items */

            /* Custom scrollbar for menu */
            .sidebar-menu::-webkit-scrollbar {
              width: 4px;
            }
            .sidebar-menu::-webkit-scrollbar-track {
              background: rgba(148, 163, 184, 0.2);
            }
            .sidebar-menu::-webkit-scrollbar-thumb {
              background: rgba(148, 163, 184, 0.5);
              border-radius: 2px;
            }
            .sidebar-menu::-webkit-scrollbar-thumb:hover {
              background: rgba(100, 116, 139, 0.7);
            }
          `}
        </style>
      </Sider>
      <Layout style={{ background: '#f5f7fb' }}>
        <Header
          style={{
            padding: '0 24px',
            background: '#ffffff',
            boxShadow: 'none',
            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                setCollapsed(!collapsed);
                setIsAutoCollapse(false);
              }}
              style={{
                fontSize: '18px',
                width: 48,
                height: 48,
                borderRadius: '12px',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                background: 'rgba(255,255,255,0.9)',
                color: '#334155',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget;
                target.style.background = colorPrimary;
                target.style.color = '#fff';
                target.style.transform = 'scale(1.05)';
                target.style.boxShadow = '0 6px 16px rgba(24, 144, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                const target = e.currentTarget;
                target.style.background = 'rgba(255,255,255,0.9)';
                target.style.color = '#334155';
                target.style.transform = 'scale(1)';
                target.style.boxShadow = 'none';
              }}
            />

            {/* Breadcrumb area - can be enhanced later */}
            <div style={{
              fontSize: '16px',
              fontWeight: '600',
              color: '#262626',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <HomeOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
              <span style={{ color: '#8c8c8c' }}>/</span>
              <span>{location.pathname.split('/')[1] || 'Dashboard'}</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Quick Actions */}
            <Space size="small">
              <Button
                type="text"
                icon={<GlobalOutlined />}
                onClick={() => navigate('/')}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  background: 'rgba(255,255,255,0.9)',
                  color: '#334155',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  target.style.background = globalStyles.gradients.primary2;
                  target.style.color = '#fff';
                  target.style.transform = 'translateY(-1px)';
                  target.style.boxShadow = '0 6px 14px rgba(30, 60, 114, 0.25)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  target.style.background = 'rgba(255,255,255,0.9)';
                  target.style.color = '#334155';
                  target.style.transform = 'translateY(0)';
                  target.style.boxShadow = 'none';
                }}
              />

              <NotificationCenter mobile={windowWidth <= 700} />

              <Button
                type="text"
                icon={<MailOutlined />}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  background: 'rgba(255,255,255,0.9)',
                  color: '#334155',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  const target = e.currentTarget;
                  target.style.background = '#4f46e5';
                  target.style.color = '#fff';
                  target.style.transform = 'translateY(-1px)';
                  target.style.boxShadow = '0 6px 14px rgba(79, 70, 229, 0.25)';
                }}
                onMouseLeave={(e) => {
                  const target = e.currentTarget;
                  target.style.background = 'rgba(255,255,255,0.9)';
                  target.style.color = '#334155';
                  target.style.transform = 'translateY(0)';
                  target.style.boxShadow = 'none';
                }}
              />
            </Space>

            <Divider type="vertical" style={{
              height: '24px',
              backgroundColor: 'rgba(15, 23, 42, 0.08)',
              margin: '0 8px'
            }} />

            {/* User Profile Dropdown */}
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div style={{
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.92)',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(24, 144, 255, 0.08)';
                e.currentTarget.style.borderColor = 'rgba(24, 144, 255, 0.2)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(24, 144, 255, 0.18)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.92)';
                e.currentTarget.style.borderColor = 'rgba(15, 23, 42, 0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(15, 23, 42, 0.08)';
              }}
              >
                <Avatar
                  size={32}
                  icon={<UserOutlined />}
                  src={getProfilePictureUrl(user?.profilePicture)}
                  style={{
                    background: globalStyles.gradients.primary2,
                    border: '2px solid rgba(255,255,255,0.95)',
                    boxShadow: '0 4px 10px rgba(24, 144, 255, 0.2)'
                  }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span style={{
                    color: '#0f172a',
                    fontSize: '14px',
                    fontWeight: '600',
                    lineHeight: '1.2'
                  }}>
                    {user?.firstName || 'User'} {user?.lastName || ''}
                  </span>
                  <span style={{
                    color: '#64748b',
                    fontSize: '12px',
                    lineHeight: '1.2'
                  }}>
                    {user?.roles?.length > 1 ? `${user.roles[0]} +${user.roles.length - 1}` : (user?.roles?.[0] || user?.role || 'User')}
                  </span>
                </div>
                <DownOutlined style={{
                  fontSize: '12px',
                  color: '#64748b',
                  transition: 'transform 0.3s ease'
                }} />
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: '16px 20px 24px',
            padding: '20px 24px 24px',
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            border: '1px solid rgba(15, 23, 42, 0.06)',
            boxShadow: '0 14px 28px rgba(15, 23, 42, 0.06)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
